import { loadOneTrust } from "src/vendor/onetrust";
import { trackEvent } from "./api/tracker";
import { getMeristemContext } from "./meristemContext";
import { isEU } from "./userSegment";

export enum LibCategories {
  strictlyNecessary = "C0001",
  performanceCookies = "C0002",
  functionalCookies = "C0003",
  targetingCookies = "C0004",
  socialMediaCookies = "C0005",
}

const waiting = new Set<() => void>();

const CONSENT_KEY = "noom_consent";
let consent: LibCategories[] = (() => {
  try {
    return JSON.parse(localStorage.getItem(CONSENT_KEY));
  } catch (err) {
    return undefined;
  }
})();
const localLoadTime = Date.now();

export function bootstrapConsent() {
  loadOneTrust().then(({ OneTrust, ActiveGroups }) => {
    try {
      const priorConsent = consent;
      const onetrustConsent = ActiveGroups?.split(/,/)?.filter(
        Boolean
      ) as LibCategories[];

      // Reset consent state to the one returned by OneTrust
      // Always do this to ensure that incorrect storage is cleared.
      if (
        ActiveGroups != null &&
        !getMeristemContext().is_forced_country_code
      ) {
        setConsent(onetrustConsent);
      }

      trackEvent("OneTrustLoaded", {
        consentMatches:
          priorConsent &&
          `${priorConsent.sort()}` === `${onetrustConsent.sort()}`,
        localConsent: priorConsent,
        onetrustConsent,
        ActiveGroups,
        onetrustLoadTime: Date.now() - localLoadTime,
      });
    } catch (err) {
      /* NOP */
    }

    // Keep consent list in sync on any future consent updates.
    OneTrust?.OnConsentChanged((event) => {
      setConsent(event.detail);

      trackEvent("CookieConsentProvided", {
        details: event.detail,
        categories: consent,
      });
    });
  });
}

function setConsent(categories: LibCategories[]) {
  consent = categories;

  // Store consent in local storage for immediate init on future visits.
  // This lets us short-circuit lib loading once user has consented.
  localStorage.setItem(CONSENT_KEY, JSON.stringify(categories));

  for (const waiter of waiting) {
    waiter();
  }
}

export function getConsent(): readonly LibCategories[] {
  return consent;
}

/**
 * Checks if all of the given categories currently have consent.
 */
export function allowCategories(categories: LibCategories[]) {
  if (!isEU()) {
    return true;
  }
  return categories
    .filter((category) => category !== LibCategories.strictlyNecessary)
    .every((category) => consent?.includes(category));
}

export function waitForConsent(categories: LibCategories[]): Promise<void> {
  return new Promise((resolve) => {
    if (allowCategories(categories)) {
      resolve();
    } else {
      waiting.add(function waiter() {
        if (allowCategories(categories)) {
          waiting.delete(waiter);
          resolve();
        }
      });
    }
  });
}
